<!--
  The categories of a subject an agent may read (a grant's scope), as
  checkboxes: those its kind allows (relaySubject.ts `categoriesFor`). Controlled: a change calls `onchange` with the new scope, and
  the boxes show `scope` as the parent has it. A grant needs one category,
  so the last one checked can't be unchecked: it says why (aria-disabled,
  so it stays reachable by keyboard and its explanation is read).
-->
<script lang="ts">
  import { categoriesFor, type RelayCategory, type SubjectKind } from '../api/relaySubject';
  import { CATEGORY_TEXT } from '../logic/relayText';
  interface Props {
    /** The subject's kind, whose categories are offered. */
    kind: SubjectKind;
    scope: readonly RelayCategory[];
    legend: string;
    onchange: (scope: RelayCategory[]) => void;
    /** Changes are ignored (a change being saved): the boxes are aria-disabled, not disabled, so a focused one keeps focus. */
    disabled?: boolean;
  }
  let { kind, scope, legend, onchange, disabled = false }: Props = $props();
  const offered = $derived(categoriesFor(kind));
  const uid = $props.id();
  function toggle(e: Event, c: RelayCategory) {
    const box = e.currentTarget as HTMLInputElement;
    const on = box.checked;
    // Ignored while disabled; the last one checked stays checked.
    if (disabled || (!on && scope.length === 1 && scope.includes(c))) { box.checked = !on; return; }
    // Shown as the parent has it, which `onchange` updates (or doesn't, when the change fails).
    box.checked = !on;
    onchange(offered.filter((x) => (x === c ? on : scope.includes(x))));
  }
</script>

<fieldset class="relay-scope" aria-disabled={disabled ? 'true' : undefined}>
  <legend>{legend}</legend>
  {#each offered as c (c)}
    {@const on = scope.includes(c)}
    <label>
      <input type="checkbox" name="{uid}-scope" value={c} checked={on} aria-disabled={disabled || (on && scope.length === 1) ? 'true' : undefined} aria-describedby={on && scope.length === 1 ? `${uid}-last` : undefined} onchange={(e) => toggle(e, c)} />
      <span>{CATEGORY_TEXT[c].label} <span class="muted">· {CATEGORY_TEXT[c].detail}</span></span>
    </label>
  {/each}
  {#if scope.length === 1}<p class="muted last" id="{uid}-last">An agent needs at least one of these.</p>{/if}
</fieldset>

<style>
  .relay-scope[aria-disabled='true'] { opacity: .55; }
  .relay-scope[aria-disabled='true'] label, .relay-scope [aria-disabled='true'] { cursor: default; }
  .relay-scope { border: 0; margin: 0; padding: 0; display: grid; gap: 4px; font-size: 13px; min-width: 0; }
  legend { padding: 0; margin-bottom: 4px; font-size: 12px; color: var(--muted); }
  label { display: flex; gap: 8px; align-items: baseline; line-height: 1.4; }
  label > span { min-width: 0; overflow-wrap: anywhere; }
  .muted { font-size: 12px; }
  .last { margin: 2px 0 0; }
</style>
